import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, useParams, Navigate, Outlet } from 'react-router-dom';
import './App.css';
import './STUI.css';

// Layout components
import { Header, Sidebar, HomePage, Footer, AuthGate } from './components/layout';
import { AlertBar, DynamicModulePage, UnderConstruction, GlobalPermissionGuard, SysAdminGuard, NavGuard, NavLoading } from './components/common';
import { NavigationProvider, useNavigation } from './context/NavigationContext';
import { NotificationProvider } from './context/NotificationContext';
import { EntraAuthProvider, ImpersonationAuthProvider, useAuth } from './context/AuthContext';
import { DatabaseProvider, useDatabase } from './context/DatabaseContext';
import { activeDbSlug, isSlugFreePage } from './services/activeDatabase';
import MsalProviderWrapper from './auth/MsalProviderWrapper';
import { isEntraAuth } from './auth/authConfig';
import AccessDeniedPage from './pages/AccessDeniedPage';
import DatabasePickerPage from './pages/DatabasePickerPage';
import AcceptInvitationPage from './pages/AcceptInvitationPage';

// Datasheet utilities
import ProjectDatasheetComposerPage from './modules/datasheets/ProjectDatasheetComposerPage';
import DeviceSpecificationUtilityPage from './modules/datasheets/DeviceSpecificationUtilityPage';

// Development pages
import { SampleTableViewPage, TaskLogPage, DevPlanPage, SchemaViewerPage, FieldManagerPage, ClaudeNotesPage, RevLogPage, ListViewConfigPage, DevelopmentDashboardPage, IconBrowserPage, ImportTemplatePage, LookupValuesPage, DisplaySettingsPage, PowerQueryPage, SyncDashboardPage, ViewSyncPage, DataEntryComposerPage, SchemaSyncPage, AdminConfigSyncPage, STUIPage, DSComposerPage, NavigationEditorPage } from './modules/development';

// Utilities pages
import { CombinedToolsPage, UtilitiesDashboardPage } from './modules/utilities';
import AuditTrailCrudPage from './modules/utilities/AuditTrailCrudPage';
import IoCountByPanelPage from './modules/utilities/IoCountByPanelPage';
import DsPackagePage from './modules/project-management/DsPackagePage';
import TransmittalsPage from './modules/project-management/TransmittalsPage';
import TransmittalsDashboardPage from './modules/project-management/TransmittalsDashboardPage';
import DistanceApproxPage from './modules/utilities/DistanceApproxPage';
import EstimateExportPage from './modules/utilities/EstimateExportPage';

// Integration pages
import { IntegrationDashboardPage, PlcPlatformsPage, PlcTagXRefPage, UdtCommentPage, ScadaTagGenerationPage, SimulationTagGenerationPage } from './modules/integration';

// Data Entry module pages
import { DataEntryModulePage, DataEntryDashboardPage } from './modules/data-entry';

// Supporting Documentation pages
import { SupportingDocsDashboardPage, SupportingDocsGroupPage } from './modules/supporting-docs';
import { LookupProvider } from './context/LookupContext';
import { DataTransferProvider } from './context/DataTransferContext';
import { ServerPdfJobResumer } from './modules/reports/pdf/serverPdfJob';
import { HelpProvider } from './context/HelpContext';

// Activity + Preferences pages
import DataTransferHistoryPage from './modules/activity/DataTransferHistoryPage';
import ReportDeliveryPage from './modules/reports/ReportDeliveryPage';
import PreferencesPage from './modules/preferences/PreferencesPage';

// Dashboard pages
import { DocumentationDashboardPage, NetworkingDashboardPage, EquipmentDashboardPage, SooDashboardPage } from './modules/dashboards';

// Report pages
import { ReportViewerPage, ReportsPage, OATCommissioningKeysPage } from './modules/reports';

// Administration pages
import { SecurityOverviewPage, SecurityDashboardPage, AdminUsersPage, RolesCapabilitiesPage, PermissionsPage, DatabasesPage, OrganizationsPage, SystemHealthPage, TableOwnershipPage, PreferenceDefaultsPage, AuditTrailAdminPage, DatabaseBackupsPage, BackupBrowserPage, ScheduledJobsPage, CredentialHealthPage } from './modules/administration';

// Config
import { PreferencesProvider, getCachedPref, getLandingPath } from './context/PreferencesContext';
import { applyTheme, readCachedTheme } from './theme/themeRuntime';

// Apply the cached theme (key + tokens) immediately on module load — prevents flash of default styles
(function () {
  const { key, tokens } = readCachedTheme();
  applyTheme(key, tokens);
}());

// Pages that do NOT need a database slug (admin DB / dev tools): SLUG_FREE_PREFIXES in
// services/activeDatabase.js, which also decides which database each request goes to.


/**
 * DbRedirect — routes bare "/" to the user's default database (server-backed
 * general.defaultDatabase preference, via the boot cache) or the picker.
 * Session slug is deliberately NOT a fallback — the default is an explicit
 * user choice, not "wherever you happened to be last".
 */
const DbRedirect = () => {
  const defaultDb = getCachedPref('general.defaultDatabase');
  if (defaultDb) return <Navigate to={getLandingPath(defaultDb)} replace />;
  return <Navigate to="/select-database" replace />;
};


/**
 * AdminLayout — shared layout for slug-free pages (admin + dev tools).
 * Uses Outlet to render the matched child route.
 */
const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, accessDenied, loading: authLoading } = useAuth();

  const [currentPage, setCurrentPageState] = useState('');
  const [sidebarTarget, setSidebarTarget] = useState(null);

  useEffect(() => {
    const pagePath = location.pathname.slice(1) || 'home';
    setCurrentPageState(pagePath);
  }, [location]);

  // Browser title — slug-free pages hit the admin DB, not a project database
  useEffect(() => {
    document.title = 'DAWN - Admin';
  }, []);

  // Navigate using router
  const setCurrentPage = (page) => {
    if (isSlugFreePage(page)) {
      navigate(`/${page}`);
    } else {
      const lastSlug = activeDbSlug() || getCachedPref('general.defaultDatabase');
      navigate(lastSlug ? `/${lastSlug}/${page === 'home' ? '' : page}` : '/select-database');
    }
  };

  const clearSidebarTarget = () => setSidebarTarget(null);

  if (!authLoading && accessDenied) return <AccessDeniedPage />;

  return (
    <div className="app-container">
      <Header
        onNavigateHome={() => {
          const lastSlug = activeDbSlug() || getCachedPref('general.defaultDatabase');
          navigate(lastSlug ? `/${lastSlug}/` : '/select-database');
        }}
        setCurrentPage={setCurrentPage}
      />
      <AlertBar />
      <div className="main-layout">
        <Sidebar
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          sidebarTarget={sidebarTarget}
          clearSidebarTarget={clearSidebarTarget}
        />
        <main className="main-content">
          <Outlet />
        </main>
      </div>
      <Footer currentPage={currentPage} isAdmin={isAdmin} />
    </div>
  );
};


/** "change-management-log" -> "Change Management Log", for a page with no navigation row. */
const humanize = (slug) => (slug || '').split(/[-/]/).filter(Boolean).map(w => w[0].toUpperCase() + w.slice(1)).join(' ');

/**
 * NavPage — renders the page a navigation row names, found by its URL slug in the person's tree:
 * an Engineering row's table (DynamicModulePage) or a DataEntry row's module. A row with no page
 * yet (a future item, a header with no landing) shows Under Construction. A slug that is not in
 * the person's tree is not theirs to open.
 */
const NavPage = () => {
  const { ready, byDestination } = useNavigation();
  const { navSlug } = useParams();
  if (!ready) return <NavLoading />;
  const row = byDestination(navSlug);
  if (!row?.open) return <Navigate to="/access-denied" replace />;
  if (row.navType === 'Engineering') {
    return <DynamicModulePage key={row.id} navKey={row.id} tableName={row.targetKey} navSlug={row.destination} />;
  }
  if (row.navType === 'DataEntry') return <DataEntryModulePage key={row.id} navKey={row.id} moduleSlug={row.targetKey} />;
  return <UnderConstruction pageName={row.label} />;
};

/** Slug-aware redirect — preserves the current dbSlug in the target path. */
const SlugRedirect = ({ to }) => {
  const { dbSlug } = useDatabase();
  return <Navigate to={`/${dbSlug}/${to}`} replace />;
};

/**
 * AppContent — layout for data pages under /:dbSlug/*.
 */
const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, isSysAdmin, accessDenied, currentUser, loading } = useAuth();
  const { dbSlug, dbDisplayName } = useDatabase();

  const [currentPage, setCurrentPageState] = useState('home');
  const [sidebarTarget, setSidebarTarget] = useState(null);

  // Browser title tracks the selected database's display name
  useEffect(() => {
    document.title = dbDisplayName ? `DAWN - ${dbDisplayName}` : 'DAWN';
  }, [dbDisplayName]);

  useEffect(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const pagePath = pathParts.slice(1).join('/') || 'home';
    setCurrentPageState(pagePath);
    // Track last-visited page per slug — feeds the 'Last Visited' landing option
    if (dbSlug && pagePath !== 'home') localStorage.setItem(`dawn.lastPage.${dbSlug}`, pagePath);
  }, [location, dbSlug]);

  const setCurrentPage = (page) => {
    if (isSlugFreePage(page)) {
      navigate(`/${page}`);
    } else {
      navigate(`/${dbSlug}/${page === 'home' ? '' : page}`);
    }
  };

  const clearSidebarTarget = () => setSidebarTarget(null);

  const zeroRoles = !loading && !isSysAdmin && currentUser && Object.keys(currentUser?.roles || {}).length === 0;
  if (accessDenied || zeroRoles) {
    return <AccessDeniedPage />;
  }

  return (
    <div className="app-container">
      <Header
        onNavigateHome={() => setCurrentPage('home')}
        setCurrentPage={setCurrentPage}
      />
      <AlertBar />
      <div className="main-layout">
        <Sidebar
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          sidebarTarget={sidebarTarget}
          clearSidebarTarget={clearSidebarTarget}
        />
        <main className="main-content">
          <Routes>
            <Route path="/" element={<HomePage setCurrentPage={setCurrentPage} isAdmin={isAdmin} />} />
            <Route path="/home" element={<HomePage setCurrentPage={setCurrentPage} isAdmin={isAdmin} />} />

            {/* Supporting Documentation dashboards */}
            <Route path="/supportingdocs-dashboard"    element={<SupportingDocsDashboardPage />} />
            <Route path="/supportingdocs-general"      element={<SupportingDocsGroupPage groupId="general" />} />
            <Route path="/supportingdocs-panels"       element={<SupportingDocsGroupPage groupId="panels" />} />
            <Route path="/supportingdocs-tags"         element={<SupportingDocsGroupPage groupId="tags" />} />
            <Route path="/supportingdocs-index"        element={<SupportingDocsGroupPage groupId="index" />} />
            <Route path="/supportingdocs-network"      element={<SupportingDocsGroupPage groupId="network" />} />
            <Route path="/supportingdocs-commissioning" element={<SupportingDocsGroupPage groupId="commissioning" />} />

            {/* Engineering tables and data entry modules: one route, the navigation row decides (NavPage) */}
            <Route path="/power-distribution-dashboard" element={<SlugRedirect to="pwr-dist" />} />
            <Route path="/data-entry-dashboard" element={<DataEntryDashboardPage />} />

            {/* Activity + Preferences */}
            <Route path="/data-transfer-history" element={<DataTransferHistoryPage />} />
            {/* Subscription delivery links, under the delivery's own database. Unguarded: the API
                decides access (recipient or SysAdmin, still holding the report's permission). */}
            <Route path="/report-delivery/:key" element={<ReportDeliveryPage />} />
            <Route path="/preferences" element={<PreferencesPage />} />

            {/* Utilities */}
            <Route path="/utilities" element={<NavGuard><UtilitiesDashboardPage /></NavGuard>} />
            <Route path="/import-template" element={<NavGuard><ImportTemplatePage /></NavGuard>} />
            <Route path="/power-query" element={<NavGuard><PowerQueryPage /></NavGuard>} />
            <Route path="/io-count-by-panel" element={<NavGuard><IoCountByPanelPage /></NavGuard>} />
            <Route path="/distance-approximation" element={<NavGuard><DistanceApproxPage /></NavGuard>} />
            <Route path="/AudTrl-CRUD" element={<NavGuard><AuditTrailCrudPage /></NavGuard>} />
            <Route path="/estimate-export" element={<NavGuard><EstimateExportPage /></NavGuard>} />
            <Route path="/ds-project-composer" element={<NavGuard><ProjectDatasheetComposerPage /></NavGuard>} />
            <Route path="/device-spec-utility" element={<NavGuard><DeviceSpecificationUtilityPage /></NavGuard>} />
            {/* Submittal Packages - Project Management > Transmittals. Per-DATABASE key, not global
                like utilities.ds.master.composer: a package belongs to one project DB, so this stays
                inside the db-slug-scoped block and out of SLUG_FREE_PREFIXES. */}
            <Route path="/ds-package" element={<NavGuard><DsPackagePage /></NavGuard>} />
            {/* Transmittals landing — one tile per log; same key as the logs it summarises. */}
            <Route path="/transmittals" element={<NavGuard><TransmittalsDashboardPage /></NavGuard>} />
            {/* Submittal / RFI / Material Transfer logs — one page, :type picks the kind. */}
            <Route path="/transmittals/:type" element={<NavGuard><TransmittalsPage /></NavGuard>} />

            {/* Integration tools */}
            <Route path="/integration-dashboard" element={<NavGuard><IntegrationDashboardPage /></NavGuard>} />
            <Route path="/io-buffer-generation" element={<NavGuard><CombinedToolsPage /></NavGuard>} />

            <Route path="/plc-platforms" element={<NavGuard><PlcPlatformsPage /></NavGuard>} />
            <Route path="/plc-tag-xref" element={<NavGuard><PlcTagXRefPage /></NavGuard>} />
            <Route path="/udt-comment-generator" element={<NavGuard><UdtCommentPage /></NavGuard>} />
            <Route path="/scada-tag-generation" element={<NavGuard><ScadaTagGenerationPage /></NavGuard>} />
            <Route path="/simulation-tag-generation" element={<NavGuard><SimulationTagGenerationPage /></NavGuard>} />

            {/* Reports — each report is a navigation row, so the viewer opens only for a report in
                the person's tree; the landing opens when any report does. */}
            <Route path="/reports" element={<NavGuard><ReportsPage /></NavGuard>} />
            <Route path="/reports/oat-commissioning-keys" element={<NavGuard><OATCommissioningKeysPage /></NavGuard>} />
            <Route path="/reports/:reportSlug" element={<NavGuard><ReportViewerPage /></NavGuard>} />
            <Route path="/documentation-dashboard" element={<DocumentationDashboardPage />} />
            <Route path="/networking-dashboard" element={<NetworkingDashboardPage />} />
            <Route path="/equipment-dashboard" element={<EquipmentDashboardPage />} />
            <Route path="/sequences-dashboard" element={<SooDashboardPage />} />

            <Route path="/:navSlug/:recordId?" element={<NavPage />} />

            {/* Catch-all */}
            <Route path="*" element={<UnderConstruction pageName={humanize(location.pathname.split('/').slice(2).join('/'))} />} />
          </Routes>
        </main>
      </div>
      <Footer currentPage={currentPage} isAdmin={isAdmin} />
    </div>
  );
};


/**
 * StyleGuideHighlighter — detects ?sgHighlight=.some-class and adds a pulsing
 * outline to every matching element on the page. Zero impact unless the param is present.
 */
const StyleGuideHighlighter = () => {
  const location = useLocation();
  const [count, setCount] = useState(0);
  const [selector, setSelector] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const highlight = params.get('sgHighlight');
    if (!highlight) {
      setSelector(null);
      setCount(0);
      return;
    }

    setSelector(highlight);

    const applyHighlights = () => {
      try {
        const elements = document.querySelectorAll(highlight);
        elements.forEach(el => el.classList.add('sg-highlight'));
        setCount(elements.length);
      } catch (e) {
        // Invalid selector — ignore
      }
    };

    // Initial apply after a short delay for async renders
    const timer = setTimeout(applyHighlights, 300);

    // MutationObserver to catch elements rendered after initial load
    const observer = new MutationObserver(applyHighlights);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearTimeout(timer);
      observer.disconnect();
      // Clean up highlights
      document.querySelectorAll('.sg-highlight').forEach(el => el.classList.remove('sg-highlight'));
    };
  }, [location.search]);

  if (!selector) return null;

  return (
    <div className="sg-highlight-banner">
      <span>Style Guide — {count} instance{count !== 1 ? 's' : ''} of <code style={{ fontFamily: 'var(--font-mono)' }}>{selector}</code> on this page</span>
      <button onClick={() => {
        document.querySelectorAll('.sg-highlight').forEach(el => el.classList.remove('sg-highlight'));
        setSelector(null);
        // Remove param from URL without page reload
        const url = new URL(window.location.href);
        url.searchParams.delete('sgHighlight');
        window.history.replaceState({}, '', url.toString());
      }}>✕ Dismiss</button>
    </div>
  );
};

/**
 * AppRoutes — all routes, rendered inside Router + Auth + Lookup providers.
 */
const AppRoutes = () => {
  return (
    <>
    <StyleGuideHighlighter />
    <Routes>
      <Route path="/" element={<DbRedirect />} />
      <Route path="/access-denied" element={<AccessDeniedPage />} />
      <Route path="/select-database" element={<DatabasePickerPage />} />
      <Route path="/accept-invitation/:token" element={<AcceptInvitationPage />} />

      {/* Slug-free admin/dev pages — use AdminLayout with Outlet */}
      <Route element={<AdminLayout />}>
        {/* Admin: hardcoded SysAdmin — never permission-key driven so we can't lock ourselves out */}
        <Route path="/admin/dashboard" element={<SysAdminGuard><SecurityDashboardPage /></SysAdminGuard>} />
        <Route path="/admin/security" element={<SysAdminGuard><SecurityOverviewPage /></SysAdminGuard>} />
        <Route path="/admin/users" element={<SysAdminGuard><AdminUsersPage /></SysAdminGuard>} />
        <Route path="/admin/roles" element={<SysAdminGuard><RolesCapabilitiesPage /></SysAdminGuard>} />
        <Route path="/admin/permissions" element={<SysAdminGuard><PermissionsPage /></SysAdminGuard>} />
        <Route path="/admin/databases" element={<SysAdminGuard><DatabasesPage /></SysAdminGuard>} />
        <Route path="/admin/organizations" element={<SysAdminGuard><OrganizationsPage /></SysAdminGuard>} />
        <Route path="/admin/table-ownership" element={<SysAdminGuard><TableOwnershipPage /></SysAdminGuard>} />
        <Route path="/admin/preference-defaults" element={<SysAdminGuard><PreferenceDefaultsPage /></SysAdminGuard>} />
        <Route path="/admin/audit-trail" element={<SysAdminGuard><AuditTrailAdminPage /></SysAdminGuard>} />
        <Route path="/admin/data-transfer-history" element={<SysAdminGuard><DataTransferHistoryPage adminView /></SysAdminGuard>} />
        <Route path="/admin/database-backups" element={<SysAdminGuard><DatabaseBackupsPage /></SysAdminGuard>} />
        <Route path="/admin/backup-browser" element={<SysAdminGuard><BackupBrowserPage /></SysAdminGuard>} />
        <Route path="/admin/scheduled-jobs" element={<SysAdminGuard><ScheduledJobsPage /></SysAdminGuard>} />
        <Route path="/admin/credential-health" element={<SysAdminGuard><CredentialHealthPage /></SysAdminGuard>} />
        <Route path="/admin/system-health" element={<SysAdminGuard><SystemHealthPage /></SysAdminGuard>} />
        <Route path="/admin/app-users" element={<SysAdminGuard><UnderConstruction pageName="Users" /></SysAdminGuard>} />
        {/* Dev tools: open when their navigation row is in the person's tree (development.main) */}
        <Route path="/development-dashboard" element={<NavGuard><DevelopmentDashboardPage /></NavGuard>} />
        <Route path="/sample-table-view" element={<GlobalPermissionGuard permKey="development.main"><SampleTableViewPage /></GlobalPermissionGuard>} />
        <Route path="/task-log" element={<NavGuard><TaskLogPage /></NavGuard>} />
        <Route path="/dev-plan" element={<NavGuard><DevPlanPage /></NavGuard>} />
        <Route path="/schema-viewer" element={<SysAdminGuard><SchemaViewerPage /></SysAdminGuard>} />
        <Route path="/navigation" element={<SysAdminGuard><NavigationEditorPage /></SysAdminGuard>} />
        <Route path="/field-manager" element={<NavGuard><FieldManagerPage /></NavGuard>} />
        <Route path="/claude-notes" element={<NavGuard><ClaudeNotesPage /></NavGuard>} />
        <Route path="/icon-browser" element={<NavGuard><IconBrowserPage /></NavGuard>} />
        <Route path="/rev-log" element={<NavGuard><RevLogPage /></NavGuard>} />
        <Route path="/standard-view-config" element={<NavGuard><ListViewConfigPage /></NavGuard>} />
        <Route path="/lookup-values" element={<NavGuard><LookupValuesPage /></NavGuard>} />
        <Route path="/display-settings" element={<NavGuard><DisplaySettingsPage /></NavGuard>} />
        <Route path="/database-sync" element={<NavGuard><SyncDashboardPage /></NavGuard>} />
        <Route path="/view-synchronizer" element={<NavGuard><ViewSyncPage /></NavGuard>} />
        <Route path="/schema-synchronizer" element={<NavGuard><SchemaSyncPage /></NavGuard>} />
        <Route path="/admin-config-sync" element={<NavGuard><AdminConfigSyncPage /></NavGuard>} />
        <Route path="/style-guide" element={<Navigate to="/stui-style-guide?tab=dynamic-module" replace />} />
        <Route path="/stui-style-guide" element={<NavGuard><STUIPage /></NavGuard>} />
        <Route path="/data-entry-view-config" element={<NavGuard><DataEntryComposerPage /></NavGuard>} />
        <Route path="/ds-Master-Composer" element={<NavGuard><DSComposerPage /></NavGuard>} />
      </Route>

      {/* Data pages — need DB slug */}
      <Route path="/:dbSlug/*" element={
        <DatabaseProvider>
          <AppContent />
        </DatabaseProvider>
      } />
    </Routes>
    </>
  );
};


// Apply stored UI zoom level on startup
const storedZoom = localStorage.getItem('dawn.uiZoom');
if (storedZoom && storedZoom !== '100%') {
  document.documentElement.style.zoom = storedZoom;
}
// Density and animations from the boot cache, so the first paint already matches.
if (localStorage.getItem('dawn.density') === 'compact') document.documentElement.classList.add('dawn-density-compact');
document.documentElement.classList.add(`dawn-motion-${['full', 'reduced', 'off'].includes(localStorage.getItem('dawn.motion')) ? localStorage.getItem('dawn.motion') : 'system'}`);

// Root App
const App = () => {
  if (isEntraAuth()) {
    return (
      <MsalProviderWrapper>
        <NotificationProvider>
          <EntraAuthProvider>
            <PreferencesProvider>
              <LookupProvider>
                <DataTransferProvider>
                  <HelpProvider>
                    <Router>
                      <AuthGate>
                        <NavigationProvider>
                          <ServerPdfJobResumer />
                          <AppRoutes />
                        </NavigationProvider>
                      </AuthGate>
                    </Router>
                  </HelpProvider>
                </DataTransferProvider>
              </LookupProvider>
            </PreferencesProvider>
          </EntraAuthProvider>
        </NotificationProvider>
      </MsalProviderWrapper>
    );
  }

  return (
    <NotificationProvider>
      <ImpersonationAuthProvider>
        <PreferencesProvider>
          <LookupProvider>
            <DataTransferProvider>
              <HelpProvider>
                <Router>
                  <AuthGate>
                    <NavigationProvider>
                      <ServerPdfJobResumer />
                      <AppRoutes />
                    </NavigationProvider>
                  </AuthGate>
                </Router>
              </HelpProvider>
            </DataTransferProvider>
          </LookupProvider>
        </PreferencesProvider>
      </ImpersonationAuthProvider>
    </NotificationProvider>
  );
};

export default App;
